import React from 'react';

import LazyLoad from '../../../../components/lazy-load/lazy-load';
import ProductListCard from '../product-list-card/product-list-card';
import ProductListPackageCard from '../product-list-package-card/product-list-package-card';
import ProductListSetCard from '../product-list-set-card/product-list-set-card';
import ProductListStaggeredCard from '../product-list-staggered-card/product-list-staggered-card';

import { useOptimisticData } from '../../../../shared/hooks/hooks';

import { updatePageNumOnBackToResultsUrl } from '../../../../shared/utils/back-to-results/back-to-results';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import { ProductData } from '../../../../shared/types/graphql-types';
import { BundleData } from '../../../../shared/types/product';
import { BACK_TO_RESULTS_PAGE_IDS } from '../../../../shared/constants/back-to-results';
import { SetSearchTypes } from '../../../../shared/constants/set-products';

import './product-list.scss';

interface ProductAndPageNum {
  pageNum: number;
  product: ProductData;
}

interface BundleAndPageNum {
  pageNum: number;
  product: BundleData;
}

interface ProductListProps {
  onAddProductClick: (
    product: ProductData | BundleData,
    idx: number,
    quantity?: number
  ) => void;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  prebuiltPackages?: BundleData[];
  prebuiltPackagesAndPageNum?: BundleAndPageNum[];
  productSetSearchType?: SetSearchTypes;
  products?: ProductData[];
  productsAndPageNum?: ProductAndPageNum[];
}

function ProductList(props: ProductListProps) {
  const {
    onAddProductClick,
    onCheckInventoryClick,
    onViewProductClick,
    prebuiltPackages,
    prebuiltPackagesAndPageNum,
    productSetSearchType,
    products,
    productsAndPageNum
  } = props;

  const optimisticData = useOptimisticData();

  function handleViewProductClick(productData) {
    if (process.env.__FF_LOAD_MORE__ === 'true') {
      const { product, pageNum } = productData;
      updatePageNumOnBackToResultsUrl(BACK_TO_RESULTS_PAGE_IDS.PLP, pageNum);
      if (product.product1) {
        optimisticData.write({ prebuiltPackage: product });
      } else {
        optimisticData.write({ product });
      }
    } else {
      optimisticData.write(productData);
    }
    onViewProductClick?.();
  }

  return (
    <div data-testid="product-list" styleName="container">
      {process.env.__FF_LOAD_MORE__ === 'true' ? (
        <>
          {Array.isArray(productsAndPageNum) &&
            productsAndPageNum.map((productObj, idx) => {
              const { product, pageNum } = productObj;
              const isSet =
                productSetSearchType === SetSearchTypes.SET &&
                isArrayWithLength(product['associatedSet']);
              let ConditionalProductListCard = ProductListCard;

              if (product?.rearProduct) {
                ConditionalProductListCard = ProductListStaggeredCard;
              } else if (isSet) {
                ConditionalProductListCard = ProductListSetCard;
              }

              return (
                <LazyLoad
                  key={`product-${product?.code || idx}`}
                  placeholder={
                    <div
                      id={
                        product && product.code
                          ? `product-code-${product.code}`
                          : ''
                      }
                    />
                  }
                >
                  <ConditionalProductListCard
                    onAddProductClick={productItem =>
                      onAddProductClick?.(product, idx, productItem?.quantity)
                    }
                    onCheckInventoryClick={onCheckInventoryClick}
                    onViewProductClick={() =>
                      handleViewProductClick(productObj)
                    }
                    pageNum={pageNum}
                    product={product}
                  />
                </LazyLoad>
              );
            })}

          {Array.isArray(prebuiltPackagesAndPageNum) &&
            prebuiltPackagesAndPageNum.map((prebuiltPackageObj, idx) => {
              const { product, pageNum } = prebuiltPackageObj;
              return (
                <LazyLoad
                  key={`product-${product.product1?.code || idx}-${
                    product.product2?.code || idx
                  }`}
                  placeholder={<div />}
                >
                  <ProductListPackageCard
                    onAddProductClick={productItem =>
                      onAddProductClick?.(product, idx, productItem?.quantity)
                    }
                    onCheckInventoryClick={onCheckInventoryClick}
                    onViewProductClick={() =>
                      handleViewProductClick(prebuiltPackageObj)
                    }
                    pageNum={pageNum}
                    prebuiltPackage={product}
                  />
                </LazyLoad>
              );
            })}
        </>
      ) : (
        <>
          {Array.isArray(products) &&
            products.map((product, idx) => {
              const isSet =
                productSetSearchType === SetSearchTypes.SET &&
                isArrayWithLength(product['associatedSet']);
              let ConditionalProductListCard = ProductListCard;

              if (product.rearProduct) {
                ConditionalProductListCard = ProductListStaggeredCard;
              } else if (isSet) {
                ConditionalProductListCard = ProductListSetCard;
              }

              return (
                <LazyLoad
                  key={`product-${product?.code || idx}`}
                  placeholder={
                    <div
                      id={
                        product && product.code
                          ? `product-code-${product.code}`
                          : ''
                      }
                    />
                  }
                >
                  <ConditionalProductListCard
                    onAddProductClick={productItem =>
                      onAddProductClick?.(product, idx, productItem?.quantity)
                    }
                    onCheckInventoryClick={onCheckInventoryClick}
                    onViewProductClick={() =>
                      handleViewProductClick({ product })
                    }
                    product={product}
                  />
                </LazyLoad>
              );
            })}

          {Array.isArray(prebuiltPackages) &&
            prebuiltPackages.map((prebuiltPackage, idx) => (
              <LazyLoad
                key={`product-${prebuiltPackage?.product1?.code || idx}-${
                  prebuiltPackage?.product2?.code || idx
                }`}
                placeholder={<div />}
              >
                <ProductListPackageCard
                  onAddProductClick={productItem =>
                    onAddProductClick?.(
                      prebuiltPackage,
                      idx,
                      productItem?.quantity
                    )
                  }
                  onCheckInventoryClick={onCheckInventoryClick}
                  onViewProductClick={() =>
                    handleViewProductClick({ prebuiltPackage })
                  }
                  prebuiltPackage={prebuiltPackage}
                />
              </LazyLoad>
            ))}
        </>
      )}
    </div>
  );
}

export default ProductList;
